'use client'

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tables } from '@/supabase/types'
import {
  IconCloud,
  IconCurrencyDollar,
  IconDots,
  IconMessageCircle,
  IconMessage2,
  IconNews,
  IconNote,
  IconPin,
  IconPlug,
  IconStar,
  IconStorm,
  IconAiAgents,
  IconUsers,
  IconWorld
} from '@tabler/icons-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  isNexusEmptyChatWidgetId,
  getInstanceKey,
  type EmptyChatWidgetId,
  type EmptyChatWidgetSettings,
  type WidgetSettingsMap
} from '@/lib/chat-empty-widgets-state'
import type { UnifiedNoteItem } from '@/app/api/notes/list/route'
import { ClockWidget } from './widgets/clock'
import { WeatherWidget } from './widgets/weather'
import { NewsCardWidget } from './widgets/news-card'
import { PlaceholderWidget } from './widgets/placeholder'
import { StickyNoteWidget } from './widgets/sticky-note'
import { FinanceWidget } from './widgets/finance'
import { cn } from '@/lib/utils'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { EMPTY_WIDGET_MUTED_CHIP_CLASS } from './widgets/widget-surface'
import { LOSI_SITE_EXPERIENCES } from '@/lib/sites/site-experiences'

interface StarredItem {
  message_id: string
  chat_id: string
  chat_name: string
  snippet: string
  created_at: string
}

interface NewsRecommendation {
  id?: string
  title: string
  url?: string | null
  image_url?: string | null
  fetched_at?: string | null
  field?: string | null
}

interface WidgetGridProps {
  enabledWidgets: EmptyChatWidgetId[]
  /** Per-instance settings keyed by getInstanceKey(enabledWidgets, index). */
  widgetSettingsMap?: WidgetSettingsMap
  /** @deprecated Use widgetSettingsMap. Fallback when widgetSettingsMap empty. */
  widgetSettings?: EmptyChatWidgetSettings
  chats: Tables<'chats'>[]
  prompts: Tables<'prompts'>[]
  notes: UnifiedNoteItem[]
  setUserInput: (value: string) => void
  onSendMessage?: (text: string) => void
  onSelectPrompt?: (prompt: Tables<'prompts'>) => void
  onStickyContentChange?: (instanceKey: string, content: string) => void
  /** When set, each widget shows an ellipsis menu (Settings / Remove) for in-context editing */
  onOpenSettings?: (instanceKey: string) => void
  onRemoveInstance?: (index: number) => void
  hasNexusAccess?: boolean
  className?: string
}

const WIDGET_IDS_WITH_SETTINGS: EmptyChatWidgetId[] = [
  'clock',
  'weather',
  'news',
  'finance',
  'sticky'
]

function WidgetTile({
  instanceKey,
  index,
  hasSettings,
  onOpenSettings,
  onRemoveInstance,
  children
}: {
  instanceKey: string
  index: number
  hasSettings: boolean
  onOpenSettings?: (instanceKey: string) => void
  onRemoveInstance?: (index: number) => void
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const showMenu = (onOpenSettings != null && hasSettings) || onRemoveInstance != null
  const contentClass = 'h-full w-full min-w-0 [&>*]:h-full [&>*]:w-full [&>*]:min-h-0 [&>*]:min-w-0'
  if (!showMenu) {
    return <div className={contentClass}>{children}</div>
  }
  return (
    <div className="group relative size-full min-w-0">
      <div className={contentClass}>{children}</div>
      <div className="absolute right-1 top-1 z-10 opacity-100 transition-opacity sm:opacity-0 sm:focus-within:opacity-100 sm:group-hover:opacity-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex size-8 items-center justify-center rounded-full border border-border/60 bg-background/90 text-muted-foreground shadow-sm backdrop-blur-sm transition-[transform,color,background-color,border-color] hover:text-foreground active:scale-95"
              aria-label={t('chat.empty_chat.widget_options', 'Widget options')}
              onClick={(e) => e.stopPropagation()}
            >
              <IconDots size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasSettings && onOpenSettings && (
              <DropdownMenuItem onClick={() => onOpenSettings(instanceKey)}>
                {t('chat.empty_chat.settings', 'Settings')}
              </DropdownMenuItem>
            )}
            {onRemoveInstance && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onRemoveInstance(index)}
              >
                {t('chat.empty_chat.remove', 'Remove')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

const WIDGET_ICONS: Record<EmptyChatWidgetId, React.ReactNode> = {
  recent_chats: <IconMessageCircle size={18} />,
  pinned_chats: <IconPin size={18} />,
  starred_messages: <IconStar size={18} />,
  recent_notes: <IconNote size={18} />,
  clock: null,
  sticky: <IconNote size={18} />,
  weather: <IconCloud size={18} />,
  finance: <IconCurrencyDollar size={18} />,
  news: <IconNews size={18} />,
  shortcuts: <IconStorm size={18} />,
  sites: <IconWorld size={18} />,
  nexus_agents: <IconAiAgents size={18} />,
  nexus_crm: <IconUsers size={18} />,
  nexus_integrations: <IconPlug size={18} />,
  nexus_conversations: <IconMessage2 size={18} />,
  nexus_notes: <IconNote size={18} />
}

const WIDGET_TITLES: Record<EmptyChatWidgetId, string> = {
  recent_chats: 'Recent Chats',
  pinned_chats: 'Pinned Chats',
  starred_messages: 'Starred Messages',
  recent_notes: 'Recent Notes',
  clock: 'Clock',
  sticky: 'Sticky',
  weather: 'Weather',
  finance: 'Finance',
  news: 'News',
  shortcuts: 'Prompts',
  sites: 'Sites',
  nexus_agents: 'Agents',
  nexus_crm: 'Contacts',
  nexus_integrations: 'Integrations',
  nexus_conversations: 'Conversations',
  nexus_notes: 'Notes'
}

const NEXUS_ROUTES: Record<
  'nexus_agents' | 'nexus_crm' | 'nexus_integrations' | 'nexus_conversations' | 'nexus_notes',
  string
> = {
  nexus_agents: '/nexus/agents',
  nexus_crm: '/nexus/crm',
  nexus_integrations: '/nexus/integrations',
  nexus_conversations: '/nexus/conversations',
  nexus_notes: '/nexus/notes'
}

function getSettings(
  widgetSettingsMap: WidgetSettingsMap | undefined,
  widgetSettings: EmptyChatWidgetSettings | undefined,
  instanceKey: string
): EmptyChatWidgetSettings {
  const fromMap = widgetSettingsMap?.[instanceKey]
  if (fromMap && Object.keys(fromMap).length > 0) return fromMap
  return widgetSettings ?? {}
}

export function WidgetGrid({
  enabledWidgets,
  widgetSettingsMap,
  widgetSettings,
  chats,
  prompts,
  notes,
  setUserInput,
  onSendMessage,
  onSelectPrompt,
  onStickyContentChange,
  onOpenSettings,
  onRemoveInstance,
  hasNexusAccess = false,
  className
}: WidgetGridProps) {
  const { t } = useTranslation()
  const { navigateToHref } = useSplitPaneNavigation()
  const [starredItems, setStarredItems] = useState<StarredItem[]>([])
  const [newsByKey, setNewsByKey] = useState<Record<string, NewsRecommendation[]>>({})
  const [newsLoadingByKey, setNewsLoadingByKey] = useState<Record<string, boolean>>({})
  const visibleWidgets = React.useMemo(
    () => enabledWidgets.filter((id) => hasNexusAccess || !isNexusEmptyChatWidgetId(id)),
    [enabledWidgets, hasNexusAccess]
  )

  const [starredLoading, setStarredLoading] = useState(false)
  useEffect(() => {
    if (!visibleWidgets.includes('starred_messages')) return
    setStarredLoading(true)
    fetch('/api/starred-messages', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => setStarredItems(data.items ?? []))
      .catch(() => setStarredItems([]))
      .finally(() => setStarredLoading(false))
  }, [visibleWidgets])

  const newsInstanceKeys = React.useMemo(() => {
    const keys: { instanceKey: string; category: string | undefined }[] = []
    visibleWidgets.forEach((id, i) => {
      if (id === 'news') {
        const instanceKey = getInstanceKey(visibleWidgets, i)
        const settings = getSettings(widgetSettingsMap, widgetSettings, instanceKey)
        keys.push({ instanceKey, category: settings.newsCategory?.trim() || undefined })
      }
    })
    return keys
  }, [visibleWidgets, widgetSettingsMap, widgetSettings])

  useEffect(() => {
    if (newsInstanceKeys.length === 0) return
    newsInstanceKeys.forEach(({ instanceKey, category }) => {
      setNewsLoadingByKey((prev) => ({ ...prev, [instanceKey]: true }))
      const params = new URLSearchParams({ limit: '5' })
      if (category) params.set('field', category)
      fetch(`/api/news/recommendations?${params}`)
        .then((r) => (r.ok ? r.json() : { recommendations: [] }))
        .then((data) => {
          setNewsByKey((prev) => ({ ...prev, [instanceKey]: data.recommendations ?? [] }))
        })
        .catch(() => setNewsByKey((prev) => ({ ...prev, [instanceKey]: [] })))
        .finally(() => setNewsLoadingByKey((prev) => ({ ...prev, [instanceKey]: false })))
    })
    const interval = setInterval(
      () => {
        newsInstanceKeys.forEach(({ instanceKey, category }) => {
          const params = new URLSearchParams({ limit: '5' })
          if (category) params.set('field', category)
          fetch(`/api/news/recommendations?${params}`)
            .then((r) => (r.ok ? r.json() : { recommendations: [] }))
            .then((data) => {
              setNewsByKey((prev) => ({ ...prev, [instanceKey]: data.recommendations ?? [] }))
            })
            .catch(() => {})
        })
      },
      5 * 60 * 1000
    )
    return () => clearInterval(interval)
  }, [newsInstanceKeys])

  const pinnedChats = chats.filter((c) => c.pinned)
  const recentChats = chats.filter((c) => !c.pinned).slice(0, 5)
  const recentNotes = notes.slice(0, 5)

  const askAndSend = (text: string) => {
    setUserInput(text)
    onSendMessage?.(text)
  }

  if (visibleWidgets.length === 0) return null

  const squareWrapper =
    'aspect-[4/3] min-h-[9.5rem] w-full min-w-0 overflow-hidden sm:aspect-square [&>*]:h-full [&>*]:w-full [&>*]:min-h-0 [&>*]:min-w-0'

  return (
    <div
      className={cn(
        'grid-auto-rows-[minmax(0,auto)] grid w-full grid-cols-1 items-start gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
        className
      )}
    >
      {visibleWidgets.map((id, index) => {
        const instanceKey = getInstanceKey(visibleWidgets, index)
        const settings = getSettings(widgetSettingsMap, widgetSettings, instanceKey)

        if (id === 'clock') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <WidgetTile
                key={instanceKey}
                instanceKey={instanceKey}
                index={index}
                hasSettings={WIDGET_IDS_WITH_SETTINGS.includes(id)}
                onOpenSettings={onOpenSettings}
                onRemoveInstance={onRemoveInstance}
              >
                <ClockWidget
                  city={settings.clockCity}
                  onClick={() =>
                    askAndSend(
                      settings.clockCity
                        ? `What time is it in ${settings.clockCity}?`
                        : 'What time is it?'
                    )
                  }
                />
              </WidgetTile>
            </div>
          )
        }

        if (id === 'weather') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <WidgetTile
                key={instanceKey}
                instanceKey={instanceKey}
                index={index}
                hasSettings={WIDGET_IDS_WITH_SETTINGS.includes(id)}
                onOpenSettings={onOpenSettings}
                onRemoveInstance={onRemoveInstance}
              >
                <WeatherWidget
                  city={settings.weatherCity}
                  useCurrentLocation={settings.weatherPreciseLocation}
                  onClick={() =>
                    askAndSend(
                      settings.weatherPreciseLocation
                        ? 'What is the weather here?'
                        : settings.weatherCity
                          ? `What is the weather in ${settings.weatherCity}?`
                          : 'What is the weather?'
                    )
                  }
                />
              </WidgetTile>
            </div>
          )
        }

        if (id === 'news') {
          const items = newsByKey[instanceKey] ?? []
          const item = items.find((r) => (r.image_url?.trim() ?? '') !== '') ?? null
          const loading = newsLoadingByKey[instanceKey]
          return (
            <div key={instanceKey} className={squareWrapper}>
              <WidgetTile
                key={instanceKey}
                instanceKey={instanceKey}
                index={index}
                hasSettings={WIDGET_IDS_WITH_SETTINGS.includes(id)}
                onOpenSettings={onOpenSettings}
                onRemoveInstance={onRemoveInstance}
              >
                <div className="size-full">
                  {loading && !item ? (
                    <div className="h-full min-h-0 animate-pulse rounded-xl bg-muted/30" />
                  ) : item ? (
                    <NewsCardWidget
                      item={item}
                      className="h-full min-h-0"
                      onClick={() => {
                        const url = item.url?.trim()
                        const text = url
                          ? `${url}\n\nTell me more about this article.`
                          : `Summarize: ${item.title}`
                        askAndSend(text)
                      }}
                    />
                  ) : (
                    <PlaceholderWidget
                      title="News"
                      icon={<IconNews size={18} />}
                      onClick={() => askAndSend('Summarize the latest news')}
                      className="h-full min-h-0"
                    />
                  )}
                </div>
              </WidgetTile>
            </div>
          )
        }

        if (id === 'starred_messages') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                key={instanceKey}
                title={WIDGET_TITLES[id]}
                icon={WIDGET_ICONS[id]}
                onClick={() => {}}
              >
                {starredLoading ? (
                  <div className="mt-2 h-8 w-full animate-pulse rounded-lg bg-muted/30" />
                ) : starredItems.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {starredItems.slice(0, 5).map((s) => (
                      <button
                        key={s.message_id}
                        type="button"
                        className={EMPTY_WIDGET_MUTED_CHIP_CLASS}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigateToHref(`/chat/${s.chat_id}?highlight=${s.message_id}`)
                        }}
                      >
                        <span className="inline-block max-w-[140px] truncate align-middle">
                          {s.chat_name}: {s.snippet || 'Starred'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('chat.empty_chat.starred_hint', 'Star messages in chat to see them here')}
                  </p>
                )}
              </PlaceholderWidget>
            </div>
          )
        }

        if (id === 'recent_chats') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                key={instanceKey}
                title={WIDGET_TITLES[id]}
                icon={WIDGET_ICONS[id]}
                onClick={() => {}}
              >
                {recentChats.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {recentChats.slice(0, 3).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={EMPTY_WIDGET_MUTED_CHIP_CLASS}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigateToHref(`/chat/${c.id}`)
                        }}
                      >
                        <span className="inline-block max-w-[120px] truncate align-middle">
                          {c.name || 'Chat'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </PlaceholderWidget>
            </div>
          )
        }

        if (id === 'pinned_chats') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                key={instanceKey}
                title={WIDGET_TITLES[id]}
                icon={WIDGET_ICONS[id]}
                onClick={() => {}}
              >
                {pinnedChats.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {pinnedChats.slice(0, 3).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={EMPTY_WIDGET_MUTED_CHIP_CLASS}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigateToHref(`/chat/${c.id}`)
                        }}
                      >
                        <span className="inline-block max-w-[120px] truncate align-middle">
                          {c.name || 'Chat'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </PlaceholderWidget>
            </div>
          )
        }

        if (id === 'shortcuts') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                key={instanceKey}
                title={t('chat.empty_chat.prompts', 'Prompts')}
                icon={WIDGET_ICONS[id]}
                onClick={() => {}}
              >
                {onSelectPrompt && prompts.length > 0 ? (
                  <ul className="mt-2 w-full space-y-1 text-xs text-muted-foreground">
                    {prompts.slice(0, 3).map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          className="truncate text-left hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation()
                            onSelectPrompt(p)
                          }}
                        >
                          {p.name.startsWith('/') ? p.name : `/${p.name}`}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('chat.empty_chat.no_prompts_yet', 'No prompts yet')}
                  </p>
                )}
              </PlaceholderWidget>
            </div>
          )
        }

        if (id === 'sites') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                title={WIDGET_TITLES[id]}
                icon={WIDGET_ICONS[id]}
                onClick={() => askAndSend(LOSI_SITE_EXPERIENCES[0].prompt)}
              >
                <p className="mt-1 text-left text-xs text-muted-foreground">
                  Websites, forms, quizzes, calculators, portals, and mini apps.
                </p>
                <div className="mt-2 grid w-full grid-cols-2 gap-1.5">
                  {LOSI_SITE_EXPERIENCES.map((experience) => (
                    <button
                      key={experience.id}
                      type="button"
                      className={cn(EMPTY_WIDGET_MUTED_CHIP_CLASS, 'min-w-0 justify-center')}
                      onClick={(event) => {
                        event.stopPropagation()
                        askAndSend(experience.prompt)
                      }}
                    >
                      <span className="truncate">{experience.label}</span>
                    </button>
                  ))}
                </div>
              </PlaceholderWidget>
            </div>
          )
        }

        if (id in NEXUS_ROUTES) {
          const route = NEXUS_ROUTES[id as keyof typeof NEXUS_ROUTES]
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                title={WIDGET_TITLES[id]}
                icon={WIDGET_ICONS[id]}
                onClick={() => navigateToHref(route)}
              />
            </div>
          )
        }

        if (id === 'recent_notes') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <PlaceholderWidget
                key={instanceKey}
	                title={WIDGET_TITLES[id]}
	                icon={WIDGET_ICONS[id]}
	                onClick={() => navigateToHref('/notes')}
              >
                {recentNotes.length > 0 ? (
                  <ul className="mt-2 w-full space-y-1 text-xs text-muted-foreground">
                    {recentNotes.slice(0, 3).map((n) => (
                      <li key={n.id}>
                        <button
                          type="button"
                          className="truncate text-left hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (n.chat_id && n.message_id) {
                              navigateToHref(`/chat/${n.chat_id}#message-${n.message_id}`)
                            } else {
	                              navigateToHref('/notes')
                            }
                          }}
                        >
                          {n.title || 'Note'}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">No notes yet</p>
                )}
              </PlaceholderWidget>
            </div>
          )
        }

        if (id === 'sticky') {
          return (
            <div key={instanceKey} className={squareWrapper}>
              <WidgetTile
                key={instanceKey}
                instanceKey={instanceKey}
                index={index}
                hasSettings={WIDGET_IDS_WITH_SETTINGS.includes(id)}
                onOpenSettings={onOpenSettings}
                onRemoveInstance={onRemoveInstance}
              >
                <StickyNoteWidget
                  content={settings.stickyContent ?? ''}
                  onContentChange={(c) => onStickyContentChange?.(instanceKey, c)}
                  accentColor={settings.stickyColor}
                />
              </WidgetTile>
            </div>
          )
        }

        if (id === 'finance') {
          const ticker = settings.financeTickers?.[0]?.trim() || 'AAPL'
          return (
            <div key={instanceKey} className={squareWrapper}>
              <WidgetTile
                instanceKey={instanceKey}
                index={index}
                hasSettings={WIDGET_IDS_WITH_SETTINGS.includes(id)}
                onOpenSettings={onOpenSettings}
                onRemoveInstance={onRemoveInstance}
              >
                <FinanceWidget
                  ticker={ticker}
                  onClick={() => askAndSend(`What is the stock price of ${ticker}?`)}
                />
              </WidgetTile>
            </div>
          )
        }

        return (
          <div key={instanceKey} className={squareWrapper}>
            <WidgetTile
              instanceKey={instanceKey}
              index={index}
              hasSettings={WIDGET_IDS_WITH_SETTINGS.includes(id)}
              onOpenSettings={onOpenSettings}
              onRemoveInstance={onRemoveInstance}
            >
              <PlaceholderWidget title={WIDGET_TITLES[id]} icon={WIDGET_ICONS[id]} />
            </WidgetTile>
          </div>
        )
      })}
    </div>
  )
}
